import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { ReviewService } from '@/lib/reviewService';
import type { Sequence } from '@/types/sequence';

// Define the type for purchase details response
interface PurchaseDetailsResponse {
  id: string;
  created_at: string;
  status: string;
}

interface DownloadToken {
  downloadUrl: string;
  token: string;
  expiresAt: string;
  attemptsAllowed: number;
}

export const useSequencePurchase = (sequenceId: string | undefined) => {
  const [isPurchaseProcessing, setIsPurchaseProcessing] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [checkingOwnership, setCheckingOwnership] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const downloadStore = useDownloadFlowStore();

  // Check ownership and purchase status when sequence or user changes
  useEffect(() => {
    const checkOwnershipAndPurchase = async () => {
      if (!sequenceId || !user) {
        setCheckingOwnership(false);
        return;
      }

      setCheckingOwnership(true);
      try {
        // Get sequence details to check ownership
        const { data: sequence, error: sequenceError } = await supabase
          .from('sequences')
          .select('user_id, price')
          .eq('id', sequenceId)
          .single();

        if (sequenceError) throw sequenceError;

        const isOwnerCheck = sequence?.user_id === user.id;
        setIsOwner(isOwnerCheck);

        // If not owner, check if purchased
        if (!isOwnerCheck) {
          const hasPurchased = await ReviewService.hasUserPurchased(sequenceId, user.id);
          setIsPurchased(hasPurchased);
        } else {
          setIsPurchased(false); // Owner doesn't need to purchase
        }
      } catch (error) {
        console.error('Error checking ownership/purchase status:', error);
      } finally {
        setCheckingOwnership(false);
      }
    };

    checkOwnershipAndPurchase();
  }, [sequenceId, user]);

  const handlePurchase = async (sequence: Sequence) => {
    if (!sequence) return;
    
    if (!user) {
      toast.error("Authentication required", {
        description: "Please sign in to purchase this sequence",
      });
      navigate('/auth');
      return;
    }
    
    setIsPurchaseProcessing(true);
    
    try {
      // Get current auth session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      // Create a Stripe checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          sequenceId: sequence.id,
          userId: user.id,
          price: sequence.price,
          title: sequence.title,
          sellerUserId: sequence.creator.id,
          songTitle: sequence.song?.title || sequence.title,
          songArtist: sequence.song?.artist || 'Unknown Artist',
          callbackUrl: `${window.location.origin}/purchase-success/${sequence.id}`
        },
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (error) throw error;
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
      
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error("Purchase failed", {
        description: "There was an error processing your purchase",
      });
    } finally {
      setIsPurchaseProcessing(false);
    }
  };
  
  const handleDownload = async (sequence: Sequence) => {
    if (!sequence || !user) return;
    
    // Check if it's a free sequence
    const isFreeSequence = sequence.price === 0;
    
    // For paid sequences, everyone (including owners) must purchase
    if (!isFreeSequence && !isPurchased) {
      // Redirect to purchase flow for paid sequences
      handlePurchase(sequence);
      return;
    }

    setIsDownloading(true);
    downloadStore.setGeneratingUrl(true);
    downloadStore.setDownloadError(null);
    
    try {
      // Start download session in store
      downloadStore.startDownloadSession({
        sequenceId: sequence.id,
        sequenceTitle: sequence.title,
        sequenceArtist: sequence.song?.artist || 'Unknown Artist',
        isOwner,
        isFree: sequence.price === 0,
        price: sequence.price,
        userId: user.id,
        // For now, we'll check if this is a charity sequence by checking the charity field
        // In the future, this should come from proper database schema
        isCharity: Boolean((sequence as any).charity || (sequence as any).is_charity),
        charityName: (sequence as any).charity?.name || (sequence as any).charity_name,
      });

      // Get current auth session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      // Find purchase ID if this is a purchased sequence (only needed for non-free sequences)
      let purchaseId: string | undefined;
      if (!isOwner && !isFreeSequence) {
        try {
          const { data: purchase, error: purchaseError } = await supabase
            .from('purchases')
            .select('id')
            .eq('sequence_id', sequence.id)
            .eq('user_id', user.id)
            .eq('status', 'completed')
            .single();
          
          if (purchaseError && purchaseError.code !== 'PGRST116') {
            console.warn('Error checking purchase status:', purchaseError);
          }
          
          purchaseId = purchase?.id;
        } catch (error) {
          console.warn('Failed to check purchase status, proceeding with download:', error);
        }
      }

      downloadStore.setDownloading(true, 25);

      console.log('Generating secure download token for sequence:', { 
        id: sequence.id, 
        title: sequence.title,
        userId: user.id,
        isOwner,
        isPurchased,
        isFree: isFreeSequence
      });

      // Step 1: Generate secure download token using server CDN endpoint
      const response = await fetch('/api/cdn/download-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          sequenceId: sequence.id,
          userId: user.id,
          purchaseId: purchaseId,
          expirationHours: 1,
          maxAttempts: 5
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Fallback for missing server endpoint (deployment issue)
        if (response.status === 404 && isFreeSequence) {
          console.log('Server endpoint not available, using fallback for free sequence');
          
          // Get sequence file URL directly from database
          const { data: sequenceData, error: fetchError } = await supabase
            .from('sequences')
            .select('storage_path, original_filename, content_type, title')
            .eq('id', sequence.id)
            .single();
          
          if (fetchError || !sequenceData?.storage_path) {
            throw new Error('Sequence file not found');
          }
          
          // Increment download count before downloading
          try {
            await fetch('/api/sequences/increment-download', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({
                sequenceId: sequence.id,
                userId: user.id
              })
            });
          } catch (error) {
            console.error('Failed to increment download count:', error);
            // Don't fail the download if count increment fails
          }
          
          // Create direct download link for free sequences
          const fileName = `${(sequenceData.title || sequence.title).replace(/[^a-zA-Z0-9\s]/g, '_')}.zip`;
          const link = document.createElement('a');
          // Use secure download route; UI can still fallback to signed URL elsewhere
          link.href = `/api/cdn/secure-download/${sequence.id}?token=${encodeURIComponent(token)}`;
          link.download = fileName;
          link.style.display = 'none';
          link.rel = 'noopener noreferrer';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          downloadStore.setDownloading(true, 100);
          
          // Show modals for free sequence downloads
          setTimeout(() => {
            if (downloadStore.shouldShowAddedToDisplayModal()) {
              downloadStore.showAddedToDisplayModal();
            }
          }, 500);
          
          toast.success("Download started", {
            description: `${fileName} is being downloaded (fallback mode)`,
          });
          
          return; // Exit early for fallback download
        }
        
        // For paid sequences or other errors, show the original error
        if (response.status === 404) {
          throw new Error('Download service temporarily unavailable. Please try again later or contact support for paid sequences.');
        }
        
        throw new Error(errorData.error || `Download request failed: ${response.status}`);
      }

      const tokenData = await response.json();
      downloadStore.setDownloadUrl(tokenData.downloadUrl, tokenData.token);
      downloadStore.setDownloading(true, 50);

      console.log('Download token generated successfully, initiating download');

      // Step 2: Preflight with HEAD to avoid false success UI
      const fileName = `${sequence.title.replace(/[^a-zA-Z0-9\s]/g, '_')}.zip`;
      const secureBase = `/api/cdn/secure-download/${sequence.id}?token=${encodeURIComponent(tokenData.token)}${
        purchaseId ? `&purchase_id=${encodeURIComponent(purchaseId)}` : ''
      }`;

      const headResp = await fetch(secureBase, { method: 'HEAD' });
      if (!headResp.ok) {
        const msg = `Download not available (${headResp.status})`;
        throw new Error(msg);
      }

      // Proceed with GET after successful HEAD
      const link = document.createElement('a');
      link.href = secureBase;
      link.download = fileName;
      link.style.display = 'none';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      downloadStore.setDownloading(true, 75);
      
      // Step 3: Show modals AFTER successful download initiation
      // This ensures they appear even if download opens in new tab
      if (!isOwner) {
        setTimeout(() => {
          // Check if should show donation modal for charity sequences
          if (downloadStore.shouldShowDonationModal()) {
            downloadStore.showDonationModal();
          } else if (downloadStore.shouldShowAddedToDisplayModal()) {
            downloadStore.showAddedToDisplayModal();
          }
        }, 500); // Small delay to ensure download has started
      }

      downloadStore.setDownloading(true, 100);
      
      toast.success("Download started", {
        description: `${fileName} is being downloaded`
      });

      // Complete download progress after a short delay
      setTimeout(() => {
        downloadStore.setDownloading(false);
      }, 2000);

    } catch (error) {
      console.error('Download error:', error);
      downloadStore.setDownloadError(error instanceof Error ? error.message : 'Download failed');
      toast.error("Download failed", {
        description: error instanceof Error ? error.message : "There was an error starting your download"
      });
    } finally {
      setIsDownloading(false);
      downloadStore.setGeneratingUrl(false);
    }
  };

  // Get appropriate button text based on ownership/purchase status
  const getDownloadButtonText = (sequence?: { price?: number }) => {
    if (checkingOwnership) return 'Checking...';
    
    // For free sequences, always show "Download"
    if (sequence?.price === 0) return 'Download';
    
    // For paid sequences
    if (isPurchased) return 'Re-download';
    
    // If not purchased (including owners), show purchase button
    return `Purchase $${sequence?.price || 0}`;
  };

  // Check if user can download (owns, has purchased, or it's free)
  // Note: We need the sequence data to check if it's free, so we'll compute this in the component
  const canDownload = isOwner || isPurchased;

  return {
    isPurchaseProcessing,
    isPurchased,
    isOwner,
    isDownloading,
    checkingOwnership,
    canDownload,
    handlePurchase,
    handleDownload,
    getDownloadButtonText,
  };
};
